import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { Button, Grid, makeStyles } from '@material-ui/core';

import DefaultTable from '../components/DefaultTable';
import PageWrapper from '../components/PageWrapper';
import * as Popover from '../components/PopoverMenu';

import { scheduleListColumns } from '../table-configs/columns-schemas';
import { fetchLine } from 'app/store/slices/line';
import { URLS } from 'app/config/urls';
import lineEndpoint from 'app/api/services/lineEndpoint';
import ListsFilters from '../components/ListsFilters';
import helpers from 'app/utils/helpers';

import InfoLineCompany from '../components/InfoLineCompany';
import ConfirmDialog from '../../../components/ConfirmDialog';
import Loading from '../../../layouts/Loading';
import SaveBox from '../components/SaveBox';
import { useTableReducer } from '../hooks/useTableReducer';
import { usePersistentFilters } from '../hooks/usePersistentFilters';

const SchedulesList = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();

    const [schedules, actions] = useTableReducer();
    const { changePage, changePageSize, resetTable, loadData: loadSchedules } = actions;

    const cdLinhaFromPath = React.useRef(match.params.cdLinha);
    const selectedLine = useSelector((state) => state.line.selected);
    const isStrip = useSelector((state) => state.line.isStrip);

    const [loading, setLoading] = useState(false);
    const [loadingRows, setLoadingRows] = useState(true);
    const [editEl, setEditEl] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [orderBy, setOrderBy] = useState('linhaQuadroHorario.cdRevisao');
    const [orderDirection, setOrderDirection] = useState('desc');
    const [canAddNewRevision, setCanAddNewRevision] = useState(false);

    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [cdToInativate, setCdToInativate] = useState(null);


    const initialFilters = useMemo(() => {
        const result = {
            campoOrdenacao: orderBy,
            direcaoOrdenacao: orderDirection,
        };
        if (cdLinhaFromPath && cdLinhaFromPath.current) {
            result.cdLinha = Number(cdLinhaFromPath.current);
        }
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cdLinhaFromPath]);

    const [filters, setFilters] = usePersistentFilters(initialFilters, 'schedules-filters');


    const [rows, setRows] = useState([]);

    const open = Boolean(editEl);

    useEffect(() => {
        (async () => {
            if (cdLinhaFromPath && cdLinhaFromPath.current) {
                await dispatch(fetchLine(cdLinhaFromPath.current));
            }
        })();
    }, [dispatch]);

    useEffect(() => {
        setLoadingRows(true);
        (async () => {
            const params = {
                page: filters.page ? filters.page : schedules.page,
                size: filters.pageSize ? filters.pageSize : schedules.pageSize,
                request: filters
            };
            const response = await lineEndpoint.getSchedules(params);
            if (response && response.status === 200) {
                if (response.data.content.length === 0) {
                    resetTable();
                } else {
                    loadSchedules(response.data);
                }
            }
            setLoadingRows(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schedules.page, schedules.pageSize, filters]);

    useEffect(() => {
        (() => {
            if (selectedLine) {
                setCanAddNewRevision(!selectedLine.hasQuadroHorarioVigenteEmRevisao);
            }
        })();
    }, [selectedLine]);

    const prepareRows = (data) => {
        if (!data) return [];
        return data.map((row) => {
            const temp = { ...row };
            temp.dtCadastro = helpers.formatarData(temp.dtCadastro, true);
            temp.dtVigencia = helpers.formatarData(temp.dtVigencia, true);
            temp.deLinhaSituacao = temp.situacao?.deLinhaSituacao;
            return temp;
        });
    };

    useEffect(() => setRows(prepareRows(schedules.content)), [schedules]);

    const handleOpenEditMenu = (event, data) => {
        setEditEl(event.currentTarget);
        setSelectedEntry(data);
    };

    const handleCloseEditMenu = () => {
        setSelectedEntry(null);
        setEditEl(null);
    };

    const handleChangePage = (e, page) => {
        changePage(page);
        const temp = { ...filters };
        temp['page'] = page;
        setFilters(temp);
    };

    const handleChangePageSize = (e) => {
        let pageSize = parseInt(e.target.value);
        changePageSize(pageSize);
        const temp = { ...filters };
        temp['pageSize'] = pageSize;
        setFilters(temp);
    };

    const handleNewScheduleButton = () => {
        let params = { cdLinha: match.params.cdLinha };
        if (isStrip) params.isStrip = 'strip';
        history.push(generatePath(URLS.LINES_MANAGE_SCHEDULES_NEW, params));
    };

    const handleChangeOrder = (event, property) => {
        let tempDirection = orderDirection;
        if (property === orderBy) {
            tempDirection = orderDirection === 'desc' ? 'asc' : 'desc';
        }
        const temp = { ...filters };
        temp['campoOrdenacao'] = `linhaQuadroHorario.${property}`;
        if (property === 'deLinhaSituacao') {
            temp['campoOrdenacao'] = `situacao.${property}`;
        }
        temp['direcaoOrdenacao'] = tempDirection;
        setOrderDirection(tempDirection);
        setOrderBy(property);
        setFilters(temp);
        handleChangePage(null, 0);
    };

    const inativate = async () => {
        setLoading(true);
        setOpenConfirmModal(false);
        const response = await lineEndpoint.inativateSchedule(cdToInativate);
        if (response && response.status === 200 && response.data) {
            window.location.reload();
        }
    };

    const renderConfirmDialog = () => {
        return (
            <ConfirmDialog
                open={openConfirmModal}
                onClose={() => setOpenConfirmModal(false)}
                handleClickNo={() => {
                    handleCloseEditMenu();
                    setOpenConfirmModal(false);
                    setCdToInativate(null);
                }}
                handleClickYes={() => {
                    handleCloseEditMenu();
                    inativate();
                }}
                text="Deseja inativar esta revisão?"
            />
        );
    };

    const handleSetSearch = (filters) => {
        resetTable();
        setFilters(filters);
    };

    const handleGoBack = () => {
        let params = { cdLinha: match.params.cdLinha };
        if (isStrip) params.isStrip = 'strip';
        history.push(generatePath(URLS.LINES_MANAGE, params));
    };

    return (
        <>
            {loading && <Loading />}
            {cdToInativate && renderConfirmDialog()}
            <PageWrapper
                title="Lista de Quadro de Horários"
                actionButton={
                    canAddNewRevision ? (
                        <Button
                            variant="contained"
                            color="primary"
                            disableElevation
                            className={classes.addButton}
                            onClick={handleNewScheduleButton}>
                            Nova revisão
                        </Button>
                    ) : (
                        <></>
                    )
                }>
                <Grid container spacing={4}>
                    <Grid item container spacing={3} direction={'row'}>
                        <InfoLineCompany selectedLine={selectedLine} />
                    </Grid>

                    <ListsFilters
                        filters={filters}
                        setFilters={handleSetSearch}
                        cdLinha={cdLinhaFromPath.current}
                        type={'schedule'}
                    />

                    <Grid item xs={12}>
                        <DefaultTable
                            columns={scheduleListColumns}
                            rows={rows}
                            loading={loadingRows}
                            onMore={handleOpenEditMenu}
                            paginationProps={{
                                rowsPerPageOptions: [5, 10, 25],
                                count: schedules.count,
                                rowsPerPage: filters.pageSize ? filters.pageSize : schedules.pageSize,
                                page: filters.page ? filters.page : schedules.page,
                                onPageChange: handleChangePage,
                                onRowsPerPageChange: handleChangePageSize,
                            }}
                            orderBy={orderBy}
                            order={orderDirection}
                            onRequestSort={handleChangeOrder}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SaveBox
                            firstButtonLabel="Voltar"
                            onCancel={handleGoBack}
                            middleButtonLabel={null}
                            lastButtonLabel={null}
                            removeSpacing
                        />
                    </Grid>
                </Grid>
            </PageWrapper>
            <Popover.SchedulesList
                onEdit={handleOpenEditMenu}
                open={open}
                anchorEl={editEl}
                handleClose={handleCloseEditMenu}
                dataPath={`${URLS.LINES_MANAGE}/${match.params.cdLinha}/schedules`}
                dataId={selectedEntry?.cdLinhaQuadroHorario}
                data={selectedEntry}
                isActive={selectedEntry?.boAtivo}
                setCdToInativate={setCdToInativate}
                setOpenConfirmModal={setOpenConfirmModal}
                isStrip={isStrip}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    addButton: {
        borderRadius: '25px',
        color: theme.palette.primary.contrastText,
    },
}));

export default SchedulesList;
